/**
 * persistFilters.ts — remember the last-applied filter set per list across
 * visits, so an operator returning to Leads/Conversations lands on the view they
 * left instead of a reset form.
 *
 * Backed by sessionStorage (survives in-tab panel navigation; clears on tab
 * close). Every access is GUARDED — when storage is unavailable (privacy mode,
 * SSR, or a test env that doesn't expose it) load/save become silent no-ops
 * rather than throwing.
 *
 * PII discipline: callers MUST strip any raw or hashed search term before
 * persisting. This module stores only coarse filter selections, never a search
 * value at rest.
 */

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const loadPersistedFilters = <T>(key: string): T | null => {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    return raw != null ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

export const savePersistedFilters = <T>(key: string, value: T): void => {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify(value));
  } catch {
    /* best-effort: ignore quota / serialisation errors */
  }
};
